import React, { useEffect, useState } from 'react';
import API from '../api/axios';
import Header from '../components/Header';
import { Plus, Edit2, Trash2, Star, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY = { name: '', description: '', image: '', price: '', duration_minutes: 60, category: '', is_featured: false };

export default function ServicesAdmin() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [sRes, cRes] = await Promise.all([API.get('/services/'), API.get('/services/categories/')]);
    setServices(sRes.data);
    setCategories(cRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openModal = (service = null) => {
    setForm(service ? { ...service, category: service.category } : EMPTY);
    setModal(service ? 'edit' : 'add');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === 'add') {
        const { data } = await API.post('/services/', form);
        setServices(s => [data, ...s]);
        toast.success('Service added!');
      } else {
        const { data } = await API.put(`/services/${form.id}/`, form);
        setServices(s => s.map(x => x.id === form.id ? data : x));
        toast.success('Service updated!');
      }
      setModal(null);
    } catch (err) {
      toast.error('Failed to save service');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await API.delete(`/services/${id}/`);
      setServices(s => s.filter(x => x.id !== id));
      toast.success('Service deleted');
    } catch { toast.error('Delete failed'); }
  };

  const filtered = services.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.category_name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="flex-1 overflow-auto">
      <Header title="Services" subtitle={`${services.length} services`} />
      <div className="p-6 space-y-4">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search services..." className="input pl-9" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button onClick={() => openModal()} className="btn-primary flex items-center gap-2">
            <Plus size={16} /> Add Service
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? [...Array(8)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-40 bg-gray-200 rounded-xl mb-4" />
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-full mb-3" />
              <div className="h-6 bg-gray-200 rounded w-1/3" />
            </div>
          )) : filtered.map(s => (
            <div key={s.id} className="card p-0 overflow-hidden hover:shadow-md transition-all">
              <div className="relative">
                <img src={s.image || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=200&fit=crop'}
                  alt={s.name} className="w-full h-40 object-cover" />
                {s.is_featured && <span className="absolute top-2 left-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">Featured</span>}
                <span className="absolute top-2 right-2 bg-white/90 text-gray-700 text-xs px-2 py-0.5 rounded-full font-medium">{s.category_name}</span>
              </div>
              <div className="p-4">
                <h4 className="font-bold text-gray-900 text-sm mb-1 truncate">{s.name}</h4>
                <p className="text-xs text-gray-500 mb-3 line-clamp-2">{s.description}</p>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center gap-1">
                    <Star size={11} className="text-yellow-400 fill-yellow-400" />
                    <span className="text-xs font-semibold">{s.rating}</span>
                  </div>
                  <span className="text-xs text-gray-400">({s.total_reviews})</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-black text-gray-900">₹{s.price}</span>
                  <div className="flex gap-1.5">
                    <button onClick={() => openModal(s)} className="w-7 h-7 bg-blue-50 text-blue-500 rounded-lg flex items-center justify-center hover:bg-blue-100">
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => handleDelete(s.id)} className="w-7 h-7 bg-red-50 text-red-500 rounded-lg flex items-center justify-center hover:bg-red-100">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="font-black text-gray-900 text-lg">{modal === 'add' ? 'Add New Service' : 'Edit Service'}</h3>
              <button onClick={() => setModal(null)} className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"><X size={16} /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
                <select className="input" value={form.category} onChange={e => set('category', e.target.value)} required>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Service Name</label>
                <input type="text" className="input" placeholder="e.g., Full Home Cleaning" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
                <textarea className="input resize-none" rows={3} value={form.description} onChange={e => set('description', e.target.value)} required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Image URL</label>
                <input type="url" className="input" placeholder="https://images.unsplash.com/..." value={form.image} onChange={e => set('image', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Price (₹)</label>
                  <input type="number" className="input" placeholder="499" value={form.price} onChange={e => set('price', e.target.value)} required min={1} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Duration (min)</label>
                  <input type="number" className="input" placeholder="60" value={form.duration_minutes} onChange={e => set('duration_minutes', e.target.value)} required min={15} />
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl">
                <input type="checkbox" id="featured" checked={form.is_featured} onChange={e => set('is_featured', e.target.checked)} className="w-4 h-4 accent-orange-500" />
                <label htmlFor="featured" className="text-sm font-medium text-gray-700 cursor-pointer">Mark as Featured Service</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="btn-primary flex-1">
                  {saving ? 'Saving...' : modal === 'add' ? 'Add Service' : 'Save Changes'}
                </button>
                <button type="button" onClick={() => setModal(null)} className="flex-1 border border-gray-200 text-gray-600 rounded-xl font-semibold text-sm py-2.5 hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
